import React, { useState, useRef, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Camera, X, Zap, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import jsQR from 'jsqr';
import api from '../utils/api';

export const CameraScanner = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [stream, setStream] = useState(null);
  const [detectedMarker, setDetectedMarker] = useState(null);
  const [error, setError] = useState(null);
  const animationRef = useRef(null);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      setError(null);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play();
      }
      
      setStream(mediaStream);
      setScanning(true);
      startScanLoop();
      toast.success('Camera active - Point at AR marker');
    } catch (err) {
      console.error('Camera error:', err);
      setError('Camera access denied. Please enable camera permissions.');
      toast.error('Failed to access camera');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    setScanning(false);
  };

  const startScanLoop = () => {
    const scan = () => {
      if (videoRef.current && canvasRef.current && scanning) {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        if (video.readyState === video.HAVE_ENOUGH_DATA) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imageData.data, imageData.width, imageData.height);
          
          if (code) {
            handleMarkerDetected(code.data);
            return;
          }
        }
      }
      animationRef.current = requestAnimationFrame(scan);
    };
    scan();
  };

  const handleMarkerDetected = async (markerData) => {
    stopCamera();
    setDetectedMarker(markerData);
    toast.success('Marker detected!');
    
    try {
      // Search for marker by ID or concept title
      const response = await api.get('/markers', { params: { is_published: true } });
      const markers = response.data;
      
      // Try to find marker by ID in QR code
      let marker = markers.find(m => m.id === markerData || markerData.includes(m.id));
      
      // If not found by ID, try by concept title
      if (!marker) {
        marker = markers.find(m => 
          markerData.toLowerCase().includes(m.concept_title.toLowerCase())
        );
      }
      
      if (marker) {
        toast.success(`Found: ${marker.concept_title}`);
        // Log analytics event
        await api.post('/analytics/events', {
          event_type: 'marker_scan',
          marker_id: marker.id,
        });
        // Navigate to 3D viewer
        setTimeout(() => {
          navigate(`/student/viewer/${marker.id}`);
        }, 1000);
      } else {
        toast.error('Marker not found in database');
        setError('This marker is not registered. Please try another image.');
      }
    } catch (error) {
      console.error('Error finding marker:', error);
      toast.error('Failed to process marker');
    }
  };

  return (
    <Layout title="Scan AR Marker">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-indigo-900 mb-2">AR Marker Scanner</h1>
          <p className="text-slate-600 text-sm sm:text-base">Point your camera at a QR code or AR marker in your textbook</p>
        </div>

        <Card className="bg-white border-slate-200 shadow-xl overflow-hidden">
          <CardContent className="p-0">
            <div className="relative bg-slate-900" style={{ aspectRatio: '16/9' }}>
              {!scanning ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-white space-y-4">
                    <Camera className="w-16 h-16 mx-auto opacity-50" />
                    <p className="text-lg">Camera not active</p>
                    <Button
                      onClick={startCamera}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      size="lg"
                    >
                      <Camera className="w-5 h-5 mr-2" />
                      Start Camera
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <video
                    ref={videoRef}
                    className="absolute inset-0 w-full h-full object-cover"
                    playsInline
                    muted
                  />
                  <canvas ref={canvasRef} className="hidden" />
                  
                  {/* Scanning overlay */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 border-4 border-dashed border-cyan-400 animate-pulse m-8 rounded-lg" />
                    <div className="absolute top-4 left-4 right-4 bg-black/50 backdrop-blur-sm p-3 rounded-lg">
                      <div className="flex items-center justify-center gap-2 text-white">
                        <Zap className="w-5 h-5 text-cyan-400 animate-pulse" />
                        <span className="text-sm font-medium">Scanning for markers...</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={stopCamera}
                    className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-red-600 hover:bg-red-700 rounded-full"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Stop Camera
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {error && (
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-red-900 mb-1">Error</h3>
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <h3 className="font-bold text-blue-900 mb-2 text-sm">How to Scan:</h3>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• <strong>Grant camera permission</strong> when prompted</li>
              <li>• <strong>Point camera</strong> at QR code or AR marker in textbook</li>
              <li>• <strong>Hold steady</strong> - the system will automatically detect</li>
              <li>• <strong>Good lighting</strong> improves detection accuracy</li>
              <li>• <strong>Ensure marker is visible</strong> within the dashed box</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <h3 className="font-bold text-amber-900 mb-2 text-sm">Alternative: Browse Manually</h3>
            <p className="text-xs text-amber-800 mb-3">
              If scanning doesn't work, you can browse subjects and chapters directly.
            </p>
            <Button
              onClick={() => navigate('/student')}
              variant="outline"
              className="w-full border-amber-300 hover:bg-amber-100"
            >
              Browse Subjects
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};